// components/Header.js - Application header component
import React, {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {cn} from '../lib/utils';
import {
	FaBars,
	FaCog,
	FaCreditCard,
	FaExchangeAlt,
	FaHistory,
	FaMoon,
	FaPaintBrush,
	FaPalette,
	FaSignInAlt,
	FaSignOutAlt,
	FaSun,
	FaTimes,
	FaUser,
	FaUserPlus
} from 'react-icons/fa';
import {AnimatePresence, motion} from 'framer-motion';
import {useTheme} from '../context/ThemeContext';
import {useAuth} from '../context/AuthContext';
import {useTranslation} from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import {Menu, Transition} from '@headlessui/react';

function Header({className}) {
	const location = useLocation();
	const navigate = useNavigate();
	const {theme, toggleTheme} = useTheme();
	const {currentUser, isAuthenticated, logout} = useAuth();
	const {t} = useTranslation();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	
	// Close mobile menu when location changes
	useEffect(() => {
		setIsMobileMenuOpen(false);
	}, [location]);
	
	useEffect(() => {
		if (currentUser) {
			console.log(currentUser);
		}
	}, [currentUser]);
	
	// Check if the current path matches the link
	const isActive = (path) => {
		if (path === '/') {
			// For home path, only match exact '/'
			return location.pathname === '/';
		}
		// For other paths, use startsWith
		return location.pathname.startsWith(path);
	};
	
	// Get the active link class
	const getLinkClass = (path) => {
		return cn(
			"flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300",
			isActive(path)
				? "glass text-white font-medium shadow-sm"
				: "text-white/80 hover:text-white hover:bg-white/10"
		);
	};
	
	// Get the active link class for mobile menu
	const getMobileLinkClass = (path) => {
		return cn(
			"flex items-center gap-2 px-4 py-3 w-full transition-all duration-300",
			isActive(path)
				? "bg-white/20 text-white font-medium"
				: "text-white/90 hover:text-white hover:bg-white/10"
		);
	};
	
	const handleLogout = async () => {
		await logout();
		navigate('/');
	};
	
	// Navigation links component to avoid duplication
	const NavLinks = ({mobile = false}) => {
		const linkClass = mobile ? getMobileLinkClass : getLinkClass;
		
		return (
			<>
				<Link to="/" className={linkClass("/")}>
					<span>{t('header.home')}</span>
				</Link>
				
				{isAuthenticated && (
					<>
						<Link to="/furniture-inpainting" className={linkClass("/furniture-inpainting")}>
							{!mobile && <FaPaintBrush className="text-sm"/>}
							<span>{t('header.furnitureInpainting')}</span>
						</Link>
						
						<Link to="/search-replace" className={linkClass("/search-replace")}>
							{!mobile && <FaExchangeAlt className="text-sm"/>}
							<span>{t('header.searchReplace')}</span>
						</Link>
						
						<Link to="/search-recolor" className={linkClass("/search-recolor")}>
							{!mobile && <FaPalette className="text-sm"/>}
							<span>{t('header.searchRecolor')}</span>
						</Link>
						
						<Link to="/image-history" className={linkClass("/image-history")}>
							{!mobile && <FaHistory className="text-sm"/>}
							<span>{t('header.imageHistory')}</span>
						</Link>
					</>
				)}
				
				{!isAuthenticated && (
					<>
						<Link to="/login" className={linkClass("/login")}>
							{!mobile && <FaSignInAlt className="text-sm"/>}
							<span>{t('header.login')}</span>
						</Link>
						
						<Link to="/register" className={linkClass("/register")}>
							{!mobile && <FaUserPlus className="text-sm"/>}
							<span>{t('header.register')}</span>
						</Link>
					</>
				)}
			</>
		);
	};
	
	return (
		<header className={cn(
			"bg-gradient-primary sticky top-0 z-50 py-4 px-6 shadow-md backdrop-blur-sm",
			className
		)}>
			<div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
				{/* Logo */}
				<Link to="/" className="text-2xl font-bold text-white flex items-center gap-2 group">
					<motion.div
						className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-md"
						whileHover={{scale: 1.1, rotate: 5}}
						whileTap={{scale: 0.95}}
						transition={{type: "spring", stiffness: 400, damping: 10}}
					>
						<span className="text-gradient-primary text-xl font-bold">DE</span>
					</motion.div>
					<motion.span
						initial={{x: 0}}
						whileHover={{x: 5}}
						transition={{type: "spring", stiffness: 400, damping: 10}}
						className="hidden sm:inline"
					>
						Decorio
					</motion.span>
				</Link>
				
				{/* Desktop Navigation */}
				<nav className="hidden md:flex items-center space-x-2">
					<NavLinks/>
					
					<LanguageSelector/>
					
					<motion.button
						className="ml-4 w-10 h-10 rounded-full flex items-center justify-center glass text-white"
						whileHover={{scale: 1.1}}
						whileTap={{scale: 0.9}}
						onClick={toggleTheme}
						aria-label={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
					>
						{theme === 'dark' ? <FaSun/> : <FaMoon/>}
					</motion.button>
					
					{isAuthenticated && (
						<div className="relative ml-4">
							<Menu as="div" className="relative inline-block text-left">
								<Menu.Button as={motion.button}
											 className="w-10 h-10 rounded-full flex items-center justify-center glass text-white relative"
											 whileHover={{scale: 1.1}}
											 whileTap={{scale: 0.9}}
											 aria-label={t('header.userMenu')}
								>
									<FaUser/>
									{/* Unverified Email Badge (Desktop) */}
									{currentUser && currentUser.isVerified === false && (
										<span
											className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 ring-2 ring-white"
											title={t('auth.emailNotVerified')}
											aria-label={t('auth.emailNotVerified')}
										>
											<span className="text-xs font-bold text-white">!</span>
										</span>
									)}
								</Menu.Button>
								
								<Transition
									as={React.Fragment}
									enter="transition ease-out duration-100"
									enterFrom="transform opacity-0 scale-95"
									enterTo="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leaveFrom="transform opacity-100 scale-100"
									leaveTo="transform opacity-0 scale-95"
								>
									<Menu.Items
										className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50 focus:outline-none">
										<div className="py-1">
											<div
												className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
												<div
													className="font-medium">{currentUser?.fullName || t('auth.user')}</div>
												<div
													className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser?.email}</div>
											</div>
											
											<Menu.Item>
												{({active}) => (
													<Link
														to="/profile"
														className={`flex items-center px-4 py-2 text-sm ${
															active
																? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
																: 'text-gray-700 dark:text-gray-200'
														}`}
													>
														<FaCog className="mr-2"/>
														{t('header.profile')}
													</Link>
												)}
											</Menu.Item>
											
											<Menu.Item>
												{({active}) => (
													<Link
														to="/subscription"
														className={`flex items-center px-4 py-2 text-sm ${
															active
																? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
																: 'text-gray-700 dark:text-gray-200'
														}`}
													>
														<FaCreditCard className="mr-2"/>
														{t('header.subscription')}
													</Link>
												)}
											</Menu.Item>
											
											<Menu.Item>
												{({active}) => (
													<button
														onClick={handleLogout}
														className={`flex items-center w-full text-left px-4 py-2 text-sm ${
															active
																? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
																: 'text-gray-700 dark:text-gray-200'
														}`}
													>
														<FaSignOutAlt className="mr-2"/>
														{t('header.logout')}
													</button>
												)}
											</Menu.Item>
										</div>
									</Menu.Items>
								</Transition>
							</Menu>
						</div>
					)}
				</nav>
				
				{/* Mobile Navigation Controls */}
				<div className="flex items-center space-x-2 md:hidden">
					<LanguageSelector/>
					
					<motion.button
						className="w-10 h-10 rounded-full flex items-center justify-center glass text-white"
						whileHover={{scale: 1.1}}
						whileTap={{scale: 0.9}}
						onClick={toggleTheme}
						aria-label={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
					>
						{theme === 'dark' ? <FaSun/> : <FaMoon/>}
					</motion.button>
					
					{isAuthenticated && (
						<div className="relative">
							<Menu as="div" className="relative inline-block text-left">
								<Menu.Button as={motion.button}
											 className="w-10 h-10 rounded-full flex items-center justify-center glass text-white relative"
											 whileHover={{scale: 1.1}}
											 whileTap={{scale: 0.9}}
											 aria-label={t('header.userMenu')}
								>
									<FaUser/>
									{/* Unverified Email Badge (Mobile) */}
									{currentUser && currentUser.isVerified === false && (
											<span
											className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 ring-2 ring-white"
											title={t('auth.emailNotVerified')}
											aria-label={t('auth.emailNotVerified')}
										>
											<span className="text-xs font-bold text-white">!</span>
										</span>
									)}
								</Menu.Button>
								
								<Transition
									as={React.Fragment}
									enter="transition ease-out duration-100"
									enterFrom="transform opacity-0 scale-95"
									enterTo="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leaveFrom="transform opacity-100 scale-100"
									leaveTo="transform opacity-0 scale-95"
								>
									<Menu.Items
										className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50 focus:outline-none">
										<div className="py-1">
											<div
												className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
												<div
													className="font-medium">{currentUser?.fullName || t('auth.user')}</div>
												<div
													className="text-xs text-gray-500 dark:text-gray-400 truncate">{currentUser?.email}</div>
											</div>
											
											<Menu.Item>
												{({active}) => (
													<Link
														to="/profile"
														className={`flex items-center px-4 py-2 text-sm ${
															active
																? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
																: 'text-gray-700 dark:text-gray-200'
														}`}
													>
														<FaCog className="mr-2"/>
														{t('header.profile')}
													</Link>
												)}
											</Menu.Item>
											
											<Menu.Item>
												{({active}) => (
													<Link
														to="/subscription"
														className={`flex items-center px-4 py-2 text-sm ${
															active
																? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
																: 'text-gray-700 dark:text-gray-200'
														}`}
													>
														<FaCreditCard className="mr-2"/>
														{t('header.subscription')}
													</Link>
												)}
											</Menu.Item>
											
											<Menu.Item>
												{({active}) => (
													<button
														onClick={handleLogout}
														className={`flex items-center w-full text-left px-4 py-2 text-sm ${
															active
																? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
																: 'text-gray-700 dark:text-gray-200'
														}`}
													>
														<FaSignOutAlt className="mr-2"/>
														{t('header.logout')}
													</button>
												)}
											</Menu.Item>
										</div>
									</Menu.Items>
								</Transition>
							</Menu>
						</div>
					)}
					
					{/* Mobile Menu Toggle Button */}
					<motion.button
						className="w-10 h-10 rounded-full flex items-center justify-center glass text-white"
						whileHover={{scale: 1.1}}
						whileTap={{scale: 0.9}}
						onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						aria-label={isMobileMenuOpen ? t('header.closeMenu') : t('header.mobileMenu')}
					>
						{isMobileMenuOpen ? <FaTimes/> : <FaBars/>}
					</motion.button>
				</div>
			</div>
			
			{/* Mobile Menu */}
			<AnimatePresence>
				{isMobileMenuOpen && (
					<motion.div
						initial={{opacity: 0, height: 0}}
						animate={{opacity: 1, height: 'auto'}}
						exit={{opacity: 0, height: 0}}
						transition={{duration: 0.3}}
						className="md:hidden mt-4 overflow-hidden"
					>
						<nav className="flex flex-col space-y-1 bg-white/10 rounded-xl p-2">
							<NavLinks mobile={true}/>
						</nav>
					</motion.div>
				)}
			</AnimatePresence>
		</header>
	);
}

export default Header;
